/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import PaymentsAPI from 'api/PaymentsAPI';
import LicensesAPI from 'api/LicensesAPI';
import Invoices from 'components/Billing/Invoices';
import LinearProgress, {
  linearProgressClasses,
} from '@mui/material/LinearProgress';
import { styled } from '@mui/material/styles';
import { Redirect } from 'react-router-dom';
import PayMethodsBox from 'components/Billing/PayMethodsBox';
import { resolveLicenseTypeForItem } from 'utils/licenseInventory';

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[800],
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: '#1a90ff',
    ...theme.applyStyles('dark', {
      backgroundColor: '#308fe8',
    }),
  },
}));

interface IBillingOverviewProps {
  cur_user: any;
  team: any;
  handleTabChange: any;
  cusId: string;
}

const BillingOverview: React.FC<IBillingOverviewProps> = ({
  cusId,
  handleTabChange,
  cur_user,
  team,
}) => {
  const [methods, setMethods] = useState<any>({ isLoading: true, data: [] });
  const [invoices, setInvoices] = useState<any>({ isLoading: true, data: [] });
  const [cusObj, setCusObj] = useState<any>({ isLoading: true, data: {} });
  const [activeSubs, setActiveSubs] = useState<any>({
    isLoading: true,
    data: [],
  });
  const [defaultChanging, setDefaultChanging] = useState<boolean>(false);
  const [licenseAssignments, setLicenseAssignments] = useState<any[]>([]);

  if (
    !cur_user.selected_team?.attributes?.team_admin?.includes(
      cur_user.basics?.email,
    )
  ) {
    return <Redirect to='/' />;
  }

  const getMainSub = (customer: any) => {
    const validSubItemTypes = ['fixed', 'base', 'credit'];
    const mainPlanOrder = [
      'bite standard',
      'bite pro',
      'bite business',
      'bite enterprise',
    ];
    const validSubTypes = [
      'BITE Standard',
      'BITE Pro',
      'BITE Business',
      'BITE Enterprise',
      ...validSubItemTypes,
    ];
    const activeSubItems: any[] = [];
    const seenSubItems = new Set<string>();
    const getSubSortWeight = (sub: any): number => {
      const subType = String(sub?.metadata?.type || '').toLowerCase();
      const subName = String(sub?.name || '').toLowerCase();
      const planIndex = mainPlanOrder.findIndex((plan) =>
        subName.includes(plan),
      );

      if (planIndex !== -1) {
        return planIndex;
      }

      if (subType === 'credit' || subName.includes('credit')) {
        return 100;
      }

      return 50;
    };

    if (customer?.data?.subscriptions?.data?.length === 0) {
      setActiveSubs({ isLoading: false, data: [] });
      return;
    }

    customer?.data?.subscriptions?.data?.forEach((sub: any) => {
      const isManageableSubscription =
        (!sub?.metadata?.type || sub?.metadata?.type !== 'api') &&
        sub?.status === 'active';

      if (!isManageableSubscription) {
        return;
      }

      sub.items.data.forEach((item: any) => {
        const hasValidItemType = validSubItemTypes.includes(
          item?.metadata?.type,
        );
        const hasValidSubType = validSubTypes.includes(
          sub?.metadata?.moduleName,
        );

        if (!hasValidItemType && !hasValidSubType) {
          return;
        }

        const dedupeKey = `${sub?.id}-${item?.id}`;

        if (seenSubItems.has(dedupeKey)) {
          return;
        }

        seenSubItems.add(dedupeKey);
        activeSubItems.push({
          ...item,
          // Kept so the item can be resolved with its subscription context
          // (module name / metadata live on either side).
          parentSubscription: sub,
          subId: sub?.id,
          cancel_at_period_end: sub?.cancel_at_period_end,
          current_period_end: sub?.current_period_end,
          status: sub?.status,
          name: item?.metadata?.moduleName || sub?.metadata?.moduleName,
        });
      });
      if (
        !sub?.items?.data?.length &&
        validSubTypes.includes(sub?.metadata?.moduleName)
      ) {
        const dedupeKey = `${sub?.id}-no-items`;
        if (!seenSubItems.has(dedupeKey)) {
          seenSubItems.add(dedupeKey);
          activeSubItems.push({
            quantity: sub?.quantity || 1,
            plan: sub?.plan,
            parentSubscription: sub,
            subId: sub?.id,
            cancel_at_period_end: sub?.cancel_at_period_end,
            current_period_end: sub?.current_period_end,
            status: sub?.status,
            name: sub?.metadata?.moduleName,
          });
        }
      }
    });
    const sortedActiveSubItems = [...activeSubItems].sort((a: any, b: any) => {
      return getSubSortWeight(a) - getSubSortWeight(b);
    });

    setActiveSubs({ isLoading: false, data: sortedActiveSubItems });
  };

  const getPaymentMethods = async (teamToQuery: any) => {
    let custId = '';
    cur_user?.stripe_cus_info?.map((cus: any) => {
      if (cus.name === teamToQuery.name) {
        custId = cus.id;
        setCusObj({ isLoading: false, data: cus });
      }
    });
    if (custId) {
      const paymentMethods = await PaymentsAPI.getCustomerPaymentMethods(
        custId,
      );
      if (paymentMethods?.success) {
        setMethods({ isLoading: false, data: paymentMethods?.result });
      }
      const cusInvoices = await PaymentsAPI.listCustomerInvoices(custId);
      if (cusInvoices?.success) {
        setInvoices({ isLoading: false, data: cusInvoices?.result });
      }
    }
  };

  const getAssignedLicenseCountForSub = (sub: any): number => {
    if (!Array.isArray(licenseAssignments) || licenseAssignments.length === 0) {
      return 0;
    }

    // `sub` is a single subscription item flattened for display, so it must be
    // resolved as the item with its subscription as context — a subscription
    // may also carry non-license items.
    const matchedType = resolveLicenseTypeForItem(sub, sub?.parentSubscription);

    if (!matchedType) {
      return 0;
    }

    return licenseAssignments.filter((assignment: any) => {
      const currentLicense = String(
        assignment?.currentLicense || '',
      ).toLowerCase();
      return currentLicense === matchedType;
    }).length;
  };

  const getSeatUsagePercentage = (sub: any): number => {
    const quantity = Number(sub?.quantity || 0);

    if (!quantity) {
      return 0;
    }

    return (getAssignedLicenseCountForSub(sub) / quantity) * 100;
  };

  useEffect(() => {
    const teamId = cur_user?.selected_team?.id;

    if (!teamId) {
      setLicenseAssignments([]);
      return;
    }

    let isCurrent = true;

    const fetchLicenses = async () => {
      const res = await LicensesAPI.getLicensesForTeam(teamId);

      if (!isCurrent) {
        return;
      }

      if (res?.success) {
        setLicenseAssignments(Array.isArray(res?.result) ? res.result : []);
      } else {
        setLicenseAssignments([]);
      }
    };

    fetchLicenses().catch(() => {
      if (!isCurrent) {
        return;
      }
      setLicenseAssignments([]);
    });

    // eslint-disable-next-line consistent-return
    return () => {
      isCurrent = false;
    };
  }, [cur_user?.selected_team?.id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!cusObj.isLoading) {
      getMainSub(cusObj);
    }
  }, [cusObj]);

  useEffect(() => {
    getPaymentMethods(team);
  }, [team]);

  return (
    <div id='billing-page-upper-container'>
      <div className='billing-page-upper-container'>
        <div className='billing-page-header-container'>
          <div className='billing-ovw-plan-container'>
            <div className='billing-ovw-plan-content'>
              {!activeSubs.isLoading && activeSubs?.data?.length === 0 && (
                <div className='billing-plan-text-container'>
                  <div className='billing-plan-text-summary'>
                    <div className='billing-plan-summary-heading'>
                      <div className='billing-plan-summary-primary-text'>
                        You currently do not have an active plan.
                      </div>
                    </div>
                    <div className='billing-plan-summary-secondary-text'>
                      Start one on the Subscriptions page.
                    </div>
                  </div>
                </div>
              )}
              {!activeSubs.isLoading && activeSubs?.data?.length > 0 && (
                <>
                  {activeSubs.data.map((sub: any) => (
                    <React.Fragment key={`${sub?.subId}-${sub?.id}`}>
                      <div className='billing-plan-text-container'>
                        <div className='billing-plan-text-summary'>
                          <div className='billing-plan-summary-heading'>
                            <div className='billing-plan-summary-primary-text'>
                              {sub?.name} x{sub?.quantity}
                            </div>
                          </div>
                        </div>
                        <div className='billing-price-summary'>
                          <div className='billing-price-dollar-wrap'>
                            <div className='billing-price-dollar-text'>$</div>
                          </div>
                          <div className='billing-main-price-text'>
                            {(sub?.quantity * sub?.plan?.amount) / 100}
                          </div>
                          <div className='billing-price-interval-wrap'>
                            <div className='billing-price-interval-text'>
                              /{sub?.plan?.interval}
                            </div>
                          </div>
                        </div>
                      </div>
                      {(sub?.metadata?.type === 'fixed' ||
                        sub?.metadata?.type === 'base') && (
                        <>
                          <div className='billing-progress-seats-bar'>
                            <div className='billing-progress-seats-text'>
                              {getAssignedLicenseCountForSub(sub)} of{' '}
                              {sub?.quantity} seats used
                            </div>
                          </div>
                          <div className='billing-seats-bar-container'>
                            <BorderLinearProgress
                              variant='determinate'
                              value={getSeatUsagePercentage(sub)}
                            />
                          </div>
                        </>
                      )}
                    </React.Fragment>
                  ))}
                </>
              )}
            </div>
            {/* <div className='billing-ovw-plan-footer'>
              <div className='billing-ovw-plan-footer-content'>
                <div className='billing-ovw-plan-footer-actions'>
                  <div
                    className='billing-ovw-plan-footer-manage-btn'
                    onClick={() => handleTabChange('Subs')}
                  >
                    <div className='billing-ovw-plan-footer-cancel-text'>
                      My Subscriptions
                    </div>
                  </div>
                </div>
              </div>
            </div> */}
          </div>
          <PayMethodsBox
            setDefaultChanging={setDefaultChanging}
            methods={methods}
            cusId={cusId}
            cusObj={cusObj}
          />
        </div>
      </div>
      <Invoices invoices={invoices.data} isLoading={invoices.isLoading} />
    </div>
  );
};

export default BillingOverview;
